import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_URL, absoluteUrl, structuredData, generateFAQSchema, generateArticleSchema } from '@/lib/seo';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  keywords?: string[];
  robots?: string;
  type?: 'website' | 'article';
  faqs?: Array<{ question: string; answer: string }>;
  article?: {
    headline: string;
    description: string;
    image: string;
    datePublished: string;
    dateModified: string;
    author?: string;
  };
}

const DEFAULT = {
  title: 'Cosmos Wave — Agence Amazon certifiée',
  description: 'Pilotage data + créa A+ pour gagner la Buy Box et scaler rentable.',
  image: '/og/default-1200x630.jpg',
  siteName: 'Cosmos Wave'
};

/**
 * Simplified SEO Head component for common use cases
 * For advanced use cases, use AdvancedSEOHead
 */
export function SEOHead({
  title,
  description,
  image,
  keywords,
  robots,
  type = 'website',
  faqs,
  article
}: SEOHeadProps) {
  const location = useLocation();
  const isProduction = typeof window !== 'undefined' && (() => {
    const hostname = window.location.hostname;
    return (
      hostname === 'cosmos-wave.com' || 
      hostname === 'www.cosmos-wave.com' ||
      (!hostname.includes('lovable.app') && !hostname.includes('localhost'))
    );
  })();
  
  // Generate canonical URL
  const canonicalUrl = absoluteUrl(location.pathname || '/');
  
  // Complete title
  const fullTitle = title ? 
    (title.includes('Cosmos Wave') ? title : `${title} | Cosmos Wave`) : 
    DEFAULT.title;
  
  // Use provided description or default
  const desc = description || DEFAULT.description;
  
  // Absolute image URL
  const ogImage = image ? absoluteUrl(image) : absoluteUrl(DEFAULT.image);
  
  // Robots meta
  const robotsMeta = robots || (isProduction ? 'index,follow' : 'noindex,nofollow');
  
  // Keywords string
  const keywordsString = keywords?.join(', ');

  return (
    <Helmet>
      {/* Basic Meta */}
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={robotsMeta} />
      
      {/* Keywords */}
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      
      {/* Security */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULT.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Base JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            structuredData.organization,
            structuredData.website,
            {
              "@type": "WebPage",
              "url": canonicalUrl,
              "name": fullTitle,
              "description": desc,
              "isPartOf": {
                "@type": "WebSite",
                "url": SITE_URL
              }
            }
          ]
        })}
      </script>
      
      {/* FAQ Schema */}
      {faqs && (
        <script type="application/ld+json">
          {JSON.stringify(generateFAQSchema(faqs))}
        </script>
      )}
      
      {/* Article Schema */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify(generateArticleSchema({
            ...article,
            url: canonicalUrl
          }))}
        </script>
      )}
    </Helmet>
  );
}

export default SEOHead;